import { useEffect, useState } from "react";
import { requestGetPictureFromUrl } from "@state/picture/PictureEffects";
import configuration from "@utils/Config";
import { blobToBase64 } from "@utils/helpers/base64-helper";
import { getToken } from "@utils/rest/RestClient";
import SignaturePicture from "@components/core/subComponents/signature/SignaturePicture";
import { Guest, Picture } from "@type/core/offices/offices.types";
import {
  requestDeleteGuestPicture,
  requestUpdateGuestPicture,
} from "@state/users/UserEffects";
import { toastError, toastSuccess } from "@utils/helpers/toast-helper";
import { useTranslation } from "react-i18next";

interface GuestSignaturePictureProps {
  guest: Guest;
}

const GuestSignaturePicture: React.FC<GuestSignaturePictureProps> = ({
  guest,
}) => {
  const { t } = useTranslation();

  const [signaturePictures, setSignaturePictures] = useState<Picture[]>([]);

  useEffect(() => {
    if (guest.guestSignaturePicture?.picture) {
      requestGetPictureFromUrl(
        `${configuration.backendBaseUrl}${guest.guestSignaturePicture.picture}&token=${getToken()}`,
      )
        .then((result) => {
          if (result.ok && result.data) {
            const blob = result.data;
            if (blob) {
              blobToBase64(blob)
                .then((base64Data) => {
                  const picture = [
                    {
                      picture: base64Data,
                      rank: 1,
                      isDefault: true,
                    },
                  ];
                  setSignaturePictures(picture);
                })
                .catch(() => {
                  toastError("Failed to convert blob to base64");
                });
            }
          }
        })
        .catch(() => {
          toastError("Error fetching picture");
        });
    } else {
      const picture = [
        {
          picture: "",
          rank: 1,
          isDefault: true,
        },
      ];
      setSignaturePictures(picture);
    }
  }, [guest]);

  const handleUpdatePicture = (rank: number, file?: File, guestId?: string) => {
    const formData = new FormData();
    formData.append("guestSignaturePicture", file || "none");
    void requestUpdateGuestPicture({
      dto: formData,
      id: guestId,
    }).then((result) => {
      const pictureUrl = result.data?.guestSignaturePicture.picture;
      void requestGetPictureFromUrl(
        `${configuration.backendBaseUrl}${pictureUrl}&token=${getToken()}`,
      ).then((result) => {
        const blob = result.data;
        if (blob) {
          blobToBase64(blob)
            .then((base64Data) => {
              const picture: Picture[] = [
                {
                  picture: base64Data,
                  rank: 1,
                  isDefault: true,
                },
              ];
              setSignaturePictures(picture);
              toastSuccess(
                t("offices.users.guests.modifyGuestPicture.success"),
              );
            })
            .catch(() => {
              toastError(t("offices.users.guests.modifyGuestPicture.error"));
            });
        }
      });
    });
  };

  const handleDeletePicture = () => {
    void requestDeleteGuestPicture({
      dto: undefined,
      id: guest.id,
    })
      .then(() => {
        const picture = [
          {
            picture: "",
            rank: 1,
            isDefault: true,
          },
        ];
        setSignaturePictures(picture);
        toastSuccess(t("offices.users.guests.deleteGuestPicture.success"));
      })
      .catch(() => {
        toastError(t("offices.users.guests.deleteGuestPicture.error"));
      });
  };

  return (
    <SignaturePicture
      signaturePictures={signaturePictures}
      onDeletePicture={handleDeletePicture}
      onUpdatePicture={(rank, file) =>
        handleUpdatePicture(rank, file, guest.id)
      }
      showButton={false}
    />
  );
};

export default GuestSignaturePicture;
